<template>
  <v-container
    class="report-dashboard productivity-dashboard py-0"
    fluid
  >
    <DashboardFilters @input="setCriteria" />
    <v-progress-linear
      :active="loading"
      color="secondary"
      indeterminate
    />
    <div
      v-if="dashboard"
      class="dashboard-content"
      :style="{ height: contentHeight }"
    >
      <div class="section-title">
        {{ $t('labels.productiveVsNonProductive') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in productivityTiles"
          :key="tile.key"
          cols="12"
          sm="4"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="8"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.productiveHoursPerDay') }}
            </div>
            <BarChart
              :chart-data="productivityPerDayData"
              :options="stackedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="chart-card non-productive-by-reason"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.nonProductiveByReason') }}
            </div>
            <DoughnutChart
              v-if="nonProductiveByReasonData.labels.length > 0"
              :chart-data="nonProductiveByReasonData"
              :options="doughnutOptions"
              :styles="chartStyles"
            />
            <div
              v-else
              class="no-data grey--text"
              :style="chartStyles"
            >
              {{ $t('labels.noRecords') }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-card
            class="chart-card"
            outlined
          >
            <div class="d-flex align-center">
              <div class="chart-title">
                {{ $t('labels.hoursBy') }}
              </div>
              <v-spacer />
              <v-btn-toggle
                v-model="breakdown"
                class="breakdown"
                color="secondary"
                dense
                mandatory
              >
                <v-btn
                  v-for="option in breakdownOptions"
                  :key="option.value"
                  small
                  text
                  :value="option.value"
                >
                  {{ option.text }}
                </v-btn>
              </v-btn-toggle>
            </div>
            <HorizontalBarChart
              :chart-data="breakdownData"
              :options="horizontalStackedBarOptions"
              :styles="barChartStyles(breakdownRows.length)"
            />
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.commitmentVsExpected') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in commitmentTiles"
          :key="tile.key"
          cols="12"
          sm="4"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.expectedVsCommittedByJobStatus') }}
            </div>
            <BarChart
              :chart-data="commitmentByJobStatusData"
              :options="groupedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="8"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.commitmentByStaff') }}
            </div>
            <v-data-table
              class="commitment-staff"
              dense
              :headers="staffHeaders"
              item-key="userId"
              :items="staffRows"
              :items-per-page="10"
              :no-data-text="$t('labels.noStaffWithWeeklyHours')"
              sort-by="difference"
            >
              <template #item.weeklyHours="{ item }">
                {{ formatNumber(item.weeklyHours) }}
              </template>
              <template #item.expected="{ item }">
                {{ formatNumber(item.expected) }}
              </template>
              <template #item.committed="{ item }">
                {{ formatNumber(item.committed) }}
              </template>
              <template #item.difference="{ item }">
                <span :class="item.difference < 0 ? 'error--text' : ''">
                  {{ item.difference > 0 ? '+' : '' }}{{ formatNumber(item.difference) }}
                </span>
              </template>
              <template #item.fulfillment="{ item }">
                {{ formatPercent(item.fulfillment) }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { BarChart, DoughnutChart, HorizontalBarChart } from '@/components/charts';
import DashboardFilters from '@/components/scheduling/reports/DashboardFilters';
import DashboardTile from '@/components/scheduling/reports/DashboardTile';
import dashboard from '@/components/scheduling/reports/dashboard';

export default {
  components: {
    BarChart,
    DashboardFilters,
    DashboardTile,
    DoughnutChart,
    HorizontalBarChart
  },
  mixins: [dashboard],
  data () {
    return {
      breakdown: this.$store.getters['account/isDepartmentManagement']() ? 'jobType' : 'department',
      retrieveAction: 'report/retrieveProductivityDashboard',
      retrieveFailMessage: 'descriptions.productivityDashboardRetrieveFail'
    };
  },
  computed: {
    breakdownData () {
      const rows = this.breakdownRows;
      return {
        labels: rows.map(row => row.name || this.$t('labels.unknown')),
        datasets: [
          this.dataset(this.$t('labels.productive'), this.colors.secondary, rows.map(row => row.productive)),
          this.dataset(this.$t('labels.nonProductive'), this.colors.accent, rows.map(row => row.nonProductive))
        ]
      };
    },
    breakdownOptions () {
      const options = [];
      if (!this.isDepartmentManagement) {
        options.push({ text: this.$tc('labels.department', 1), value: 'department' });
      }
      options.push(
        { text: this.$tc('labels.jobType', 1), value: 'jobType' },
        { text: this.$t('labels.jobStatus'), value: 'jobStatus' }
      );
      return options;
    },
    breakdownRows () {
      const keys = {
        department: 'byDepartment',
        jobStatus: 'byJobStatus',
        jobType: 'byJobType'
      };
      return this.dashboard.productivity[keys[this.breakdown]];
    },
    commitmentByJobStatusData () {
      const rows = this.dashboard.commitment.byJobStatus;
      return {
        labels: rows.map(row => row.name || this.$t('labels.unknown')),
        datasets: [
          this.dataset(this.$t('labels.expected'), '#BDBDBD', rows.map(row => row.expected)),
          this.dataset(this.$t('labels.committed'), this.colors.secondary, rows.map(row => row.committed))
        ]
      };
    },
    commitmentTiles () {
      const { commitment } = this.dashboard;
      let belowDetail = this.$t('labels.ofStaff', { count: this.formatNumber(commitment.staffCount) });
      if (commitment.withoutWeeklyHours > 0) {
        belowDetail += `, ${this.$t('labels.withoutWeeklyHours', { count: this.formatNumber(commitment.withoutWeeklyHours) })}`;
      }
      return [
        {
          key: 'committed',
          label: this.$t('labels.committedHours'),
          value: this.formatNumber(commitment.committed),
          detail: this.$t('labels.ofExpectedHours', { hours: this.formatNumber(commitment.expected) })
        },
        {
          key: 'fulfillment',
          label: this.$t('labels.fulfillment'),
          value: this.formatPercent(commitment.percent),
          detail: ''
        },
        {
          key: 'below-commitment',
          label: this.$t('labels.belowCommitment'),
          value: this.formatNumber(commitment.belowCount),
          detail: belowDetail
        }
      ];
    },
    nonProductiveByReasonData () {
      return this.doughnutData(this.dashboard.productivity.nonProductiveByFlag, 'hours');
    },
    productivityPerDayData () {
      const { byDate } = this.dashboard.productivity;
      return {
        labels: this.dayLabels(byDate),
        datasets: [
          this.dataset(this.$t('labels.productive'), this.colors.secondary, byDate.map(day => day.productive)),
          this.dataset(this.$t('labels.nonProductive'), this.colors.accent, byDate.map(day => day.nonProductive))
        ]
      };
    },
    productivityTiles () {
      const { productivity } = this.dashboard;
      return [
        {
          key: 'productive',
          label: this.$t('labels.productiveHours'),
          value: this.formatNumber(productivity.productive),
          detail: ''
        },
        {
          key: 'non-productive',
          label: this.$t('labels.nonProductiveHours'),
          value: this.formatNumber(productivity.nonProductive),
          detail: ''
        },
        {
          key: 'productivity',
          label: this.$t('labels.productivity'),
          value: this.formatPercent(productivity.percent),
          detail: this.$t('labels.productivityDetail')
        }
      ];
    },
    staffHeaders () {
      return [
        { text: this.$t('labels.employeeName'), value: 'name' },
        { text: this.$t('labels.homeDepartment'), value: 'homeDepartmentName' },
        { text: this.$t('labels.jobStatus'), value: 'jobStatusName' },
        { align: 'end', text: this.$t('labels.weeklyHours'), value: 'weeklyHours' },
        { align: 'end', text: this.$t('labels.expected'), value: 'expected' },
        { align: 'end', text: this.$t('labels.committed'), value: 'committed' },
        { align: 'end', text: this.$t('labels.difference'), value: 'difference' },
        { align: 'end', text: this.$t('labels.fulfillment'), value: 'fulfillment' }
      ];
    },
    staffRows () {
      return this.dashboard.commitment.staff.map(row => ({
        ...row,
        difference: Math.round((row.committed - row.expected) * 10) / 10,
        fulfillment: row.expected > 0 ? Math.round(row.committed / row.expected * 1000) / 10 : null
      }));
    }
  }
};
</script>

<style lang="scss">
@import "src/assets/sass/report-dashboard.scss";
</style>
